<template>
  <div>
    <!-- Filter and search projects -->
    <div v-if="filterable">
      <h3 class="text-md mb-4 text-center font-normal text-secondary-dark dark:text-ternary-light sm:text-xl">
        Search projects by title or filter by category
      </h3>
      <div class="flex flex-col justify-between gap-2 border-b border-primary-light pb-3 dark:border-secondary-dark sm:flex-row">
        <div class="flex w-full justify-between sm:w-auto">
          <span class="hidden cursor-pointer rounded-l-xl border border-gray-200 bg-primary-light p-2.5 shadow-sm dark:border-secondary-dark dark:bg-ternary-dark sm:block">
            <PhMagnifyingGlass class="text-ternary-dark dark:text-ternary-light" />
          </span>
          <input
            id="name"
            v-model="searchProject"
            class="sm:text-md w-full rounded-lg border border-gray-200 bg-secondary-light py-2 pl-3 pr-1 text-sm text-primary-dark dark:border-secondary-dark dark:bg-ternary-dark dark:text-ternary-light sm:rounded-l-none sm:px-4"
            name="name"
            type="search"
            required
            placeholder="Search Projects"
            aria-label="Name"
          >
        </div>
        <ProjectsFilter v-model="selectedType" />
      </div>
    </div>

    <!-- Projects grid -->
    <p v-if="!filteredProjects.length" class="mt-12 text-center text-lg text-gray-600 dark:text-gray-400">
      No projects match your search.
    </p>

    <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" :class="{ 'mt-8': filterable }">
      <div
        v-for="(project, index) in filteredProjects"
        :key="project.slug"
        v-reveal="(index % 3) * 90"
        class="group relative overflow-hidden rounded-2xl bg-card-gradient shadow-soft transition-all duration-500 hover:-translate-y-2 hover:shadow-soft-lg dark:bg-card-gradient-dark"
        aria-label="Single Project"
      >
        <NuxtLink :to="`/projects/${project.slug}`" class="block">
          <!-- Image container with overlay -->
          <div class="relative overflow-hidden">
            <img
              :src="project.images[0]?.src"
              :alt="project.name"
              width="800"
              height="600"
              loading="lazy"
              class="h-64 w-full object-cover transition-transform duration-500 group-hover:scale-110"
            >
            <!-- Gradient overlay -->
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            <!-- Hover overlay with icon -->
            <div class="absolute inset-0 flex items-center justify-center opacity-0 transition-all duration-300 group-hover:opacity-100">
              <div class="flex size-12 items-center justify-center rounded-full bg-white/90 shadow-lg dark:bg-gray-800/90">
                <PhArrowRight class="size-6 text-accent" />
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="p-6">
            <div class="mb-3">
              <div class="inline-flex items-center rounded-full bg-accent/10 px-3 py-1 dark:bg-accent/20">
                <span class="text-xs font-medium text-accent dark:text-accent">{{ PROJECT_TYPE_LABELS[project.type] }}</span>
              </div>
            </div>

            <h3 class="mb-2 text-xl font-bold text-ternary-dark transition-colors duration-300 group-hover:text-accent dark:text-ternary-light dark:group-hover:text-accent">
              {{ project.name }}
            </h3>

            <p class="leading-relaxed text-gray-600 dark:text-gray-400">
              {{ project.subtitle }}
            </p>

            <!-- Bottom border accent -->
            <div class="mt-4 h-1 w-0 rounded-full bg-gradient-to-r from-accent to-gradient-end transition-all duration-500 group-hover:w-full" />
          </div>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Project, ProjectType } from '~/utils/projects'
import { PhArrowRight, PhMagnifyingGlass } from '@phosphor-icons/vue'
import ProjectsFilter from '~/components/Projects/ProjectsFilter.vue'
import { PROJECT_TYPE_LABELS } from '~/utils/projects'

const props = withDefaults(defineProps<{ projects: Project[], filterable?: boolean }>(), { filterable: true })

const selectedType = ref<ProjectType | ''>('')
const searchProject = ref('')

const filteredProjects = computed(() => {
  const query = searchProject.value.trim().toLowerCase()
  return props.projects.filter(project =>
    (!selectedType.value || project.type === selectedType.value)
    && (!query || project.name.toLowerCase().includes(query)),
  )
})
</script>
